/** Parent picker для категории — то же что PrimaryCategoryPicker,
 *  но добавляет опцию «Без родителя (корневая категория)».
 *  Также исключает саму редактируемую категорию и её потомков из
 *  списка (нельзя сделать категорию родителем себя или своего потомка).
 */

import { Command } from "cmdk";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface AdminCategoryRaw {
  id: number;
  parent_id: number | null;
  slug: string;
  products_count?: number;
  translations: Array<{ language: string; name: string }>;
}

interface FlatNode {
  id: number;
  name: string;
  parent_id: number | null;
  depth: number;
  path: string;
}

function flatten(categories: AdminCategoryRaw[]): FlatNode[] {
  const byParent = new Map<number | null, AdminCategoryRaw[]>();
  for (const c of categories) {
    const arr = byParent.get(c.parent_id ?? null) ?? [];
    arr.push(c);
    byParent.set(c.parent_id ?? null, arr);
  }
  function nameOf(c: AdminCategoryRaw) {
    return (
      c.translations.find((t) => t.language === "ru")?.name ??
      c.translations[0]?.name ??
      c.slug
    );
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => nameOf(a).localeCompare(nameOf(b), "ru"));
  }

  const result: FlatNode[] = [];
  function walk(parentId: number | null, depth: number, parentPath: string) {
    const kids = byParent.get(parentId) ?? [];
    for (const c of kids) {
      const name = nameOf(c);
      const path = parentPath ? `${parentPath} › ${name}` : name;
      result.push({
        id: c.id,
        name,
        parent_id: c.parent_id,
        depth,
        path,
      });
      walk(c.id, depth + 1, path);
    }
  }
  walk(null, 0, "");
  return result;
}

/** Возвращает Set всех id потомков `rootId` (рекурсивно). */
function descendantIds(
  categories: AdminCategoryRaw[],
  rootId: number,
): Set<number> {
  const childrenOf = new Map<number, number[]>();
  for (const c of categories) {
    if (c.parent_id != null) {
      const arr = childrenOf.get(c.parent_id) ?? [];
      arr.push(c.id);
      childrenOf.set(c.parent_id, arr);
    }
  }
  const out = new Set<number>();
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    for (const ch of childrenOf.get(id) ?? []) stack.push(ch);
  }
  return out;
}

function useClickOutside(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);
  return ref;
}

export function ParentCategoryPicker({
  value,
  onChange,
  excludeId,
}: {
  value: number | null;
  onChange: (id: number | null) => void;
  /** id редактируемой категории — её и потомков прячем из списка */
  excludeId?: number;
}) {
  const { data: categories, isLoading } = useQuery({
    queryKey: ["admin-categories-raw"],
    queryFn: async () => {
      const { data } = await api.get<AdminCategoryRaw[]>("/categories");
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(open, () => setOpen(false));

  const excluded = useMemo(
    () =>
      excludeId && categories
        ? descendantIds(categories, excludeId)
        : new Set<number>(),
    [categories, excludeId],
  );

  const flat = useMemo(
    () =>
      categories
        ? flatten(categories).filter((c) => !excluded.has(c.id))
        : [],
    [categories, excluded],
  );

  const selected = value != null ? flat.find((c) => c.id === value) : null;
  const triggerLabel = value == null
    ? "Без родителя (корневая категория)"
    : selected?.path ?? `id ${value}`;

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="outline"
        role="combobox"
        onClick={() => setOpen((o) => !o)}
        className="w-full justify-between font-normal"
        disabled={isLoading}
      >
        <span className="truncate text-left">{triggerLabel}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border border-border bg-popover shadow-md">
          <Command shouldFilter>
            <Command.Input
              autoFocus
              placeholder="Поиск категории…"
              className="h-9 w-full border-b border-border bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground"
            />
            <Command.List className="max-h-72 overflow-auto p-1">
              <Command.Empty className="px-3 py-3 text-sm text-muted-foreground text-center">
                Ничего не найдено
              </Command.Empty>
              <Command.Item
                value="__root__ Без родителя корневая"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent aria-selected:bg-accent"
              >
                <Check
                  className={`h-4 w-4 ${value == null ? "opacity-100" : "opacity-0"}`}
                />
                <span className="italic text-muted-foreground">
                  Без родителя (корневая категория)
                </span>
              </Command.Item>
              {flat.map((c) => (
                <Command.Item
                  key={c.id}
                  value={c.path}
                  onSelect={() => {
                    onChange(c.id);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent aria-selected:bg-accent"
                >
                  <Check
                    className={`h-4 w-4 ${
                      c.id === value ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  <span style={{ paddingLeft: c.depth * 12 }}>{c.name}</span>
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </div>
      )}
    </div>
  );
}
